import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { axiosInstanceV2 } from '../../../../services/axios';
import { usePricingPlans } from '../../../../hooks/usePricingPlans';
import useSubscription from '../../../../hooks/useSubscription';
import PageLoader from '../../../../component/PageLoader';

/**
 * Subscription Sandbox — drives the real /change-plan/preview endpoint against
 * the org's real subscription for every plan and billing cycle at once.
 *
 * The point is to make plan-change behaviour verifiable without waiting for a
 * billing cycle to end and without moving money: preview is read-only, so the
 * whole matrix can be refreshed as often as you like. Executing a change is a
 * separate, deliberate action per row.
 */

const CHANGE_TONE = {
	upgrade: { label: 'Upgrade', bg: '#F0FFE6', fg: '#3F8A17' },
	downgrade: { label: 'Downgrade', bg: '#FFF4E5', fg: '#B45309' },
	downgrade_to_free: { label: 'Downgrade to Free', bg: '#FFF4E5', fg: '#B45309' },
	no_change: { label: 'Current', bg: '#EEF2F7', fg: '#475569' },
	invalid: { label: 'Not allowed', bg: '#FDECEC', fg: '#B42318' },
};

const APPLIES_LABEL = {
	immediately: 'Immediately',
	on_renewal: 'On renewal',
	on_expiry: 'On expiry',
};

const rupees = (paise) =>
	paise === null || paise === undefined ? '—' : `₹${(Number(paise) / 100).toFixed(2)}`;

function Pill({ tone, children }) {
	return (
		<span
			className="px-2 py-1 rounded text-nowrap"
			style={{ backgroundColor: tone.bg, color: tone.fg, fontSize: '12px', fontWeight: 600 }}
		>
			{children}
		</span>
	);
}

export default function SubscriptionSandbox() {
	const { plans, loading: plansLoading } = usePricingPlans({ enabled: true });
	const { subscription, isSubscriptionRecurring } = useSubscription();

	const [rows, setRows] = useState([]);
	const [running, setRunning] = useState(false);
	const [expanded, setExpanded] = useState(null);
	const [executing, setExecuting] = useState(null);

	// Every plan x cycle the org could move to.
	const targets = useMemo(() => {
		const out = [];
		for (const plan of plans ?? []) {
			for (const entry of plan.billing ?? []) {
				if (!entry.razorpay_plan_id) continue;
				out.push({
					key: `${plan.key}-${entry.period}`,
					planName: plan.name,
					period: entry.period,
					amount: entry.amount,
					razorpay_plan_id: entry.razorpay_plan_id,
					cardLabel: entry.change_type,
				});
			}
		}
		return out;
	}, [plans]);

	const runAll = useCallback(async () => {
		if (!targets.length) return;
		setRunning(true);
		try {
			const authToken = localStorage.getItem('authToken');
			const results = await Promise.all(
				targets.map(async (target) => {
					try {
						const response = await axiosInstanceV2.post(
							'/subscription/change-plan/preview',
							{
								razorpay_plan_id: target.razorpay_plan_id,
								billing_period: target.period,
							},
							{ headers: { Authorization: `Bearer ${authToken}` } },
						);
						return { target, preview: response.data.data, error: null };
					} catch (error) {
						return {
							target,
							preview: null,
							error: error?.response?.data?.message ?? error.message,
						};
					}
				}),
			);
			setRows(results);
		} finally {
			setRunning(false);
		}
	}, [targets]);

	useEffect(() => {
		if (targets.length) runAll();
	}, [targets, runAll]);

	const execute = async (row) => {
		const confirmed = window.confirm(
			`Really change this organisation to ${row.target.planName} (${row.target.period})?\n\n` +
				`This is a REAL plan change against Razorpay, not a preview.`,
		);
		if (!confirmed) return;

		setExecuting(row.target.key);
		try {
			const authToken = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.post(
				'/subscription/change-plan',
				{
					razorpay_plan_id: row.target.razorpay_plan_id,
					billing_period: row.target.period,
					is_recurring: isSubscriptionRecurring,
				},
				{ headers: { Authorization: `Bearer ${authToken}` } },
			);
			const result = response.data.data;
			toast.success(result?.message ?? 'Plan change submitted.');
			await runAll();
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Plan change failed.');
		} finally {
			setExecuting(null);
		}
	};

	if (plansLoading) return <PageLoader />;

	return (
		<div className="p-4">
			<div className="d-flex justify-content-between align-items-start mb-3 gap-3 flex-wrap">
				<div>
					<h5 className="mb-1">Subscription Sandbox</h5>
					<p
						className="mb-0"
						style={{ fontSize: '13px', color: '#64748B' }}
					>
						Previews are read-only — nothing is charged and nothing is scheduled. Use them to
						confirm how each plan change would behave before running one for real.
					</p>
				</div>
				<button
					className="btn btn-outline-primary"
					onClick={runAll}
					disabled={running}
				>
					{running ? 'Checking…' : 'Re-run previews'}
				</button>
			</div>

			<div
				className="mb-4 p-3 rounded"
				style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}
			>
				<div
					className="d-flex gap-4 flex-wrap"
					style={{ fontSize: '13px' }}
				>
					<span>
						<strong>Current plan:</strong> {subscription?.plan?.name ?? '—'}
					</span>
					<span>
						<strong>Billing:</strong> {subscription?.billing_period ?? '—'}
					</span>
					<span>
						<strong>Model:</strong> {isSubscriptionRecurring ? 'Recurring' : 'One-time'}
					</span>
					<span>
						<strong>Status:</strong> {subscription?.status_display?.label ?? subscription?.status}
					</span>
				</div>
			</div>

			<div className="table-responsive">
				<table
					className="table align-middle"
					style={{ fontSize: '13px' }}
				>
					<thead>
						<tr>
							<th>Target plan</th>
							<th>Cycle</th>
							<th>Price</th>
							<th>Verdict</th>
							<th>Applies</th>
							<th>Payable now</th>
							<th>Card says</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const p = row.preview;
							const tone = CHANGE_TONE[p?.change_type] ?? CHANGE_TONE.invalid;
							const isOpen = expanded === row.target.key;
							// The pricing card and the API must agree — a mismatch here is
							// exactly the class of bug where a button reads "Downgrade" and
							// then charges immediately.
							const cardAgrees =
								!p?.change_type ||
								(row.target.cardLabel ?? '').toLowerCase().includes(p.change_type.split('_')[0]);

							return (
								<React.Fragment key={row.target.key}>
									<tr>
										<td>{row.target.planName}</td>
										<td>{row.target.period}</td>
										<td>{rupees(row.target.amount)}</td>
										<td>
											{row.error ? (
												<Pill tone={CHANGE_TONE.invalid}>Error</Pill>
											) : (
												<Pill tone={tone}>{tone.label}</Pill>
											)}
										</td>
										<td>{APPLIES_LABEL[p?.applies_at] ?? '—'}</td>
										<td>
											{p?.prorated_amount !== null && p?.prorated_amount !== undefined
												? rupees(p.prorated_amount)
												: p?.prorated_amount_note
													? 'Prorated by Razorpay'
													: '—'}
										</td>
										<td>
											<span
												style={{
													color: cardAgrees ? '#475569' : '#B42318',
													fontWeight: cardAgrees ? 400 : 600,
												}}
											>
												{row.target.cardLabel ?? '—'}
												{!cardAgrees && ' ⚠︎'}
											</span>
										</td>
										<td className="text-end text-nowrap">
											<button
												className="btn btn-sm btn-link"
												onClick={() => setExpanded(isOpen ? null : row.target.key)}
											>
												{isOpen ? 'Hide' : 'Details'}
											</button>
											<button
												className="btn btn-sm btn-outline-danger"
												disabled={!p?.allowed || executing === row.target.key}
												onClick={() => execute(row)}
												title={p?.blocked_reason ?? 'Runs a real plan change'}
											>
												{executing === row.target.key ? 'Running…' : 'Execute'}
											</button>
										</td>
									</tr>

									{isOpen && (
										<tr>
											<td
												colSpan={8}
												style={{ backgroundColor: '#F8FAFC' }}
											>
												{row.error && <div className="text-danger mb-2">{row.error}</div>}
												{p?.blocked_reason && (
													<div className="mb-2">
														<strong>Blocked:</strong> {p.blocked_reason}
													</div>
												)}
												{p?.reason && (
													<div className="mb-2">
														<strong>Why:</strong> {p.reason}
													</div>
												)}
												{p?.effective_date && (
													<div className="mb-2">
														<strong>Effective:</strong>{' '}
														{new Date(p.effective_date).toLocaleString()}
													</div>
												)}
												{p?.requires_checkout && (
													<div className="mb-2">Requires checkout (no mandate to charge).</div>
												)}
												{p?.usage_over_new_limits?.length > 0 && (
													<div className="mb-2">
														<strong>Usage over the new plan&apos;s limits:</strong>
														<ul className="mb-0">
															{p.usage_over_new_limits.map((u) => (
																<li key={u.key}>
																	{u.feature}: {u.used} used, new limit {u.new_limit}
																</li>
															))}
														</ul>
													</div>
												)}
												{p?.feature_changes?.length > 0 && (
													<details>
														<summary>{p.feature_changes.length} feature change(s)</summary>
														<ul className="mb-0 mt-2">
															{p.feature_changes.map((f) => (
																<li key={f.key}>
																	{f.feature}: {String(f.from ?? 'not included')} →{' '}
																	{String(f.to ?? 'not included')}
																</li>
															))}
														</ul>
													</details>
												)}
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
